/**
 * LicensesAdminPage — CRUD admin cho bảng cp_license (Sprint 5).
 * Columns: license_cert_no, cp_code, valid_from, valid_to, is_valid, si, sd, note.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm, Controller } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import DateTextField from '@/components/DateTextField'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListAdminLicensesQuery,
  useCreateAdminLicenseMutation,
  useUpdateAdminLicenseMutation,
  useDeleteAdminLicenseMutation,
} from '@/store/api/refsCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { AdminLicense } from '@/types/api'

interface LicFormValues {
  license_cert_no: string
  cp_code: string
  valid_from: string
  valid_to: string
  is_valid: boolean
  si: string
  sd: string
  note: string
}

const EMPTY: LicFormValues = {
  license_cert_no: '',
  cp_code: '',
  valid_from: '',
  valid_to: '',
  is_valid: true,
  si: '',
  sd: '',
  note: '',
}

export function LicensesAdminPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<AdminLicense | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<AdminLicense | null>(null)

  const { data, isLoading, error, isFetching } = useListAdminLicensesQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createAdminLicense, { isLoading: creating }] = useCreateAdminLicenseMutation()
  const [updateAdminLicense, { isLoading: updating }] = useUpdateAdminLicenseMutation()
  const [deleteAdminLicense, { isLoading: deleting }] = useDeleteAdminLicenseMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset, control, setValue, watch } = useForm<LicFormValues>({ defaultValues: EMPTY })
  const validFrom = watch('valid_from')
  const validTo = watch('valid_to')

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        license_cert_no: editing.license_cert_no,
        cp_code: editing.cp_code ?? '',
        valid_from: editing.valid_from ?? '',
        valid_to: editing.valid_to ?? '',
        is_valid: editing.is_valid ?? true,
        si: editing.si ?? '',
        sd: editing.sd ?? '',
        note: editing.note ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.license_id, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'license_cert_no', headerName: 'License No', width: 180 },
      { field: 'cp_code', headerName: 'Vendor', width: 120 },
      { field: 'valid_from', headerName: 'Valid From', width: 120 },
      { field: 'valid_to', headerName: 'Valid To', width: 120 },
      {
        field: 'is_valid',
        headerName: 'Valid',
        width: 100,
        renderCell: (params) =>
          params.value ? (
            <Chip label="Valid" color="success" size="small" />
          ) : (
            <Chip label="Expired" color="error" size="small" />
          ),
      },
      { field: 'si', headerName: 'SI', width: 100 },
      { field: 'sd', headerName: 'SD', width: 100 },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as AdminLicense) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as AdminLicense) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Partial<AdminLicense> = {
      license_cert_no: values.license_cert_no.trim(),
      cp_code: values.cp_code.trim(),
      valid_from: values.valid_from || null,
      valid_to: values.valid_to || null,
      is_valid: values.is_valid,
      si: values.si || null,
      sd: values.sd || null,
      note: values.note || null,
    }
    try {
      if (isEdit && editing) {
        await updateAdminLicense({ id: editing.license_id, body: payload }).unwrap()
        snack.success(`Updated license "${editing.license_cert_no}"`)
      } else {
        await createAdminLicense(payload).unwrap()
        snack.success(`Created license "${values.license_cert_no}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteAdminLicense(confirmDelete.license_id).unwrap()
      snack.success(`Deleted license "${confirmDelete.license_cert_no}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Licenses (Admin)</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New License
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="License no, vendor..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as AdminLicense)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit License' : 'New License'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
        maxWidth="md"
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="License Cert No *" size="small" fullWidth {...register('license_cert_no')} disabled={isEdit} />
          <TextField label="Code (cp_code)" size="small" fullWidth {...register('cp_code')} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <input type="hidden" {...register('valid_from')} />
            <input type="hidden" {...register('valid_to')} />
            <DateTextField label="Valid From" size="small" value={validFrom} onChange={(value) => setValue('valid_from', value, { shouldDirty: true, shouldTouch: true })} sx={{ flex: 1 }} />
            <DateTextField label="Valid To" size="small" value={validTo} onChange={(value) => setValue('valid_to', value, { shouldDirty: true, shouldTouch: true })} sx={{ flex: 1 }} />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField label="SI" size="small" {...register('si')} sx={{ flex: 1 }} />
            <TextField label="SD" size="small" {...register('sd')} sx={{ flex: 1 }} />
          </Box>
          <TextField label="Note" size="small" fullWidth multiline rows={2} {...register('note')} />
          <Controller
            name="is_valid"
            control={control}
            render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Is Valid" />
            )}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete License"
        message={confirmDelete ? `Are you sure you want to delete license "${confirmDelete.license_cert_no}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default LicensesAdminPage
